<template>
  <footer class="footer-modern">
    <v-container>
      <v-row class="pb-8" justify-md="space-between">
        <!-- Logo and Description -->
        <v-col cols="12" md="4" class="mb-8 mb-md-0">
          <div class="footer-logo mb-4">
            <div class="footer-logo-icon">
              <svg class="footer-mark" viewBox="0 0 32 32" fill="none" aria-hidden="true">
                <path
                  d="M4 15 L16 5 L28 15 M7 12.5 V26 H25 V12.5"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  class="footer-spark"
                  d="M16 14.5 C16.6 17.2 17.8 18.4 20.5 19 C17.8 19.6 16.6 20.8 16 23.5 C15.4 20.8 14.2 19.6 11.5 19 C14.2 18.4 15.4 17.2 16 14.5 Z"
                />
              </svg>
            </div>
            <div class="footer-logo-text">
              <div class="logo-cleano-footer">Cleano</div>
              <div class="logo-management-footer">MANAGEMENT</div>
            </div>
          </div>
          <p class="footer-description">
            Professional cleaning business management software for jobs, crews, quotes,
            appointments, engage customers, and grow your business.
          </p>
        </v-col>

        <!-- Company -->
        <v-col cols="12" sm="4" md="2" class="mb-8 mb-md-0">
          <h4 class="footer-heading mb-4">Company</h4>
          <ul class="footer-links">
            <li><router-link to="/why">Why Cleano</router-link></li>
            <li><router-link to="/features">Features</router-link></li>
            <li><router-link to="/about">About Us</router-link></li>
            <li><router-link to="/tribzz">Tribzz</router-link></li>
            <li><router-link to="/blog">Blog</router-link></li>
          </ul>
        </v-col>

        <!-- Support -->
        <v-col cols="12" sm="4" md="2" class="mb-8 mb-md-0">
          <h4 class="footer-heading mb-4">Support</h4>
          <ul class="footer-links">
            <li><router-link to="/documentation">Documentation</router-link></li>
            <li><router-link to="/help">Help Center</router-link></li>
            <li><router-link to="/contact">Contact Us</router-link></li>
            <li><router-link to="/demo">Request Demo</router-link></li>
            <li><router-link to="/login">Client Portal</router-link></li>
          </ul>
        </v-col>

        <!-- Legal -->
        <v-col cols="12" sm="4" md="2" class="mb-8 mb-md-0">
          <h4 class="footer-heading mb-4">Legal</h4>
          <ul class="footer-links">
            <li><router-link to="/privacy">Privacy Policy</router-link></li>
            <li><router-link to="/terms">Terms of Service</router-link></li>
            <li><router-link to="/cookies">Cookie Policy</router-link></li>
            <li><router-link to="/security">Security</router-link></li>
          </ul>
        </v-col>

        <!-- Contact: shown when `contact` below has an email, phone or location -->
        <v-col v-if="hasContact" cols="12" sm="6" md="2">
          <h4 class="footer-heading mb-4">Contact</h4>
          <div class="footer-contact">
            <div v-if="contact.email" class="contact-item mb-2">
              <v-icon small color="#5BB4E5" class="mr-2">mdi-email</v-icon>
              <a :href="`mailto:${contact.email}`">{{ contact.email }}</a>
            </div>
            <div v-if="contact.phone" class="contact-item mb-2">
              <v-icon small color="#5BB4E5" class="mr-2">mdi-phone</v-icon>
              <a :href="`tel:${contact.phone.replace(/[^\d+]/g, '')}`">{{ contact.phone }}</a>
            </div>
            <div v-if="contact.location" class="contact-item">
              <v-icon small color="#5BB4E5" class="mr-2">mdi-map-marker</v-icon>
              <span>{{ contact.location }}</span>
            </div>
          </div>
        </v-col>
      </v-row>

      <v-divider class="mb-6" style="border-color: rgba(255,255,255,0.09) !important;"></v-divider>

      <div class="footer-bottom">
        <p class="copyright mb-0">
          © {{ currentYear }} Cleano Management. All rights reserved.
        </p>
        <div class="footer-meta">
          <span class="meta-item">$150/Month + $499 onboarding</span>
          <span class="meta-divider">•</span>
          <span class="meta-item">Cancel Anytime</span>
        </div>
      </div>
    </v-container>
  </footer>
</template>

<script>
export default {
  name: 'AppFooter',
  data() {
    return {
      // Fill these in when you're ready. Empty values are simply not shown,
      // and the whole Contact column stays hidden until at least one is set.
      contact: {
        email: 'ricosevere@codeforgedev.com',
        phone: '(339) 241-8725',
        location: ''
      }
    };
  },
  computed: {
    currentYear() {
      return new Date().getFullYear();
    },
    hasContact() {
      return !!(this.contact.email || this.contact.phone || this.contact.location);
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&display=swap');

.footer-modern {
  --accent: #5BB4E5;
  --accent-light: #A6DBF7;
  --accent-dark: #3A8CC2;
  --text-primary: #EEF3F8;
  --text-secondary: rgba(238, 243, 248, 0.68);
  --text-muted: rgba(238, 243, 248, 0.48);
  --border-subtle: rgba(255, 255, 255, 0.09);

  background: #05070C;
  border-top: 1px solid var(--border-subtle);
  color: var(--text-secondary);
  padding: 64px 0 24px;
  font-family: 'Syne', -apple-system, BlinkMacSystemFont, sans-serif;
}

.footer-logo {
  display: flex;
  align-items: center;
  gap: 12px;
}

.footer-logo-icon {
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.footer-mark {
  width: 34px;
  height: 34px;
  color: var(--accent-light);
}

.footer-spark {
  fill: var(--accent);
}

.footer-logo-text {
  display: flex;
  flex-direction: column;
}

.logo-cleano-footer {
  font-family: 'Syne', sans-serif;
  font-size: 1.3rem;
  font-weight: 700;
  color: var(--text-primary);
  line-height: 1;
  letter-spacing: -0.01em;
}

.logo-management-footer {
  font-size: 0.65rem;
  font-weight: 600;
  letter-spacing: 2.5px;
  color: var(--accent);
  margin-top: 3px;
}

.footer-description {
  font-size: 0.9rem;
  line-height: 1.7;
  color: var(--text-muted);
  max-width: 320px;
}

.footer-heading {
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--text-primary);
  letter-spacing: 0.02em;
}

.footer-links {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.footer-links a {
  color: var(--text-secondary);
  text-decoration: none;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  display: inline-block;
}

.footer-links a:hover {
  color: var(--accent-light);
  transform: translateX(4px);
}

.footer-links a:focus-visible,
.contact-item a:focus-visible {
  outline: 2px solid var(--accent-light);
  outline-offset: 3px;
  border-radius: 4px;
}

.footer-contact {
  font-size: 0.9rem;
}

.contact-item {
  display: flex;
  align-items: center;
}

.contact-item a {
  color: var(--text-secondary);
  text-decoration: none;
  word-break: break-all;
}

.contact-item a:hover {
  color: var(--accent-light);
}

.footer-bottom {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

.copyright {
  font-size: 0.85rem;
  color: var(--text-muted);
}

.footer-meta {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.85rem;
  color: var(--text-muted);
}

.meta-item {
  font-weight: 600;
}

.meta-divider {
  opacity: 0.5;
}

@media (max-width: 600px) {
  .footer-bottom {
    flex-direction: column;
    align-items: flex-start;
  }
}

@media (prefers-reduced-motion: reduce) {
  .footer-links a {
    transition: none;
  }

  .footer-links a:hover {
    transform: none;
  }
}
</style>